<script setup lang="ts">
import type { ContentItem, RegisterResultDto } from "@edco/apollo";
import type { Register } from "~/models/generated/Register";

defineProps<{
  content: ContentItem<Register>;
}>();

const route = useRoute();
const router = useRouter();
const auth = useAuthStore();

// Get the web account ID from the URL to preload a web account
const wid = route.query.wid?.toString();

const hasRegistered = ref( false );
const competition = ref<ContentItem | null>( null );

const contentResource = useContentDeliveryResource();
const { data: linkSchools } = await contentResource.getByTypeSingle( "linkSchools" );
const { data: selectPreferences } = await contentResource.getByTypeSingle( "selectPreferences" );

const selectPreferencesUrl = computed( () => selectPreferences.value?.route.path || "/select-preferences" );
const linkSchoolsUrl = computed( () => linkSchools.value?.route.path || "/link-schools" );

if ( route.query.cid ) {
  const competitionId = route.query.cid as string;
  const competitionData = await contentResource.getById( competitionId );
  competition.value = competitionData.data.value;
}

const loginUrl = computed( () => {
  let result = "/login";
  if ( competition.value ) {
    result += `?cid=${competition.value.id}`;
  }
  return result;
} );

const onRegistered = async ( result: RegisterResultDto | undefined ) => {
  if ( !result?.success ) {
    return;
  }

  hasRegistered.value = true;

  let url = linkSchoolsUrl.value;

  if ( result.loggedInAccount?.webAccount?.webAccountId?.toLowerCase() === wid?.toLowerCase() ) {
    // The preloaded web account is the web account we have registered so skip to the select preferences page
    url = selectPreferencesUrl.value;
  }

  if ( !url ) {
    // Nowhere to go so redirect to the home page
    url = "/";
  }

  // Give backend time to process registration
  setTimeout( async () => {
    await navigateTo( url );
  }, 350 );
};

const logOut = async () => {
  await auth.logOut();
  await router.replace( "/register" );
};

const putid = 3;
</script>

<template>
  <main class="register-page">
    <section>
      <page-banner
        :content="content.properties"
        class="register-page__banner" />

      <div class="container">
        <RichText
          v-if="content.properties.intro"
          class="register-page__intro"
          :content="content.properties.intro" />

        <div class="register-page__content">
          <div class="register-page__editor">
            <div
              v-if="auth.isLoggedIn && !hasRegistered"
              class="register-page__logged-in">
              <p>
                You are already logged in as
                <a
                  href="/my-account">{{ auth.webAccount?.firstName + " " + auth.webAccount?.lastName }}</a>.
                Not you?
              </p>

              <AButton
                style="margin-top: 10px"
                @click="logOut">
                Log out
              </AButton>
            </div>

            <div
              v-else
              class="register-page__form">
              <h2 class="register-page__form-heading">
                {{ content.properties.formHeading || 'Create your account' }}
              </h2>

              <RegistrationWizard
                show-password
                :user-type-parent-id="putid"
                :web-account-id="wid"
                pre-load-guest-only
                auto-login
                check-email-at-start
                metadata-single-column
                skip-address-step
                :field-label-config="{
                  finishButton: 'Continue',
                }"
                primary-button-type="secondary"
                primary-button-size="lg"
                @registered="onRegistered" />
            </div>
          </div>
        </div>
        <div
          v-if="!auth.isLoggedIn"
          class="log-in">
          <p>If you already have an account, you can</p>
          <a-button
            look="outline"
            variant="secondary"
            :to="loginUrl">
            Log in here
          </a-button>
        </div>
      </div>
    </section>
  </main>
</template>

<style lang="scss">
.register-page {
  min-height: 60vh;
  margin-bottom: 60px;

  &__banner {
    margin-top: -20px;
    margin-bottom: 30px;
  }

  &__intro {
    max-width: 40rem;
    margin-left: auto;
    margin-right: auto;
    margin-bottom: 2rem;
  }

  &__content {
    max-width: 40rem;
    margin-left: auto;
    margin-right: auto;
    background: #f1f8f7;
    border-radius: 0.5rem;
    padding: 1.5rem;
  }

  &__editor {
    background: #fff;
    border-radius: 0.5rem;
    padding: 1.5rem;
  }

  &__logged-in {
    text-align: center;
  }

  &__form {
    .form-item .form-label {
      padding-top: 0;
    }

    .row {
      justify-content: center;
    }

    legend {
      font-weight: bold;
    }
  }

  &__button {
    min-width: 140px;
    border-radius: 40px;
  }

  h1 {
    text-align: center;
    font-size: 2rem;
    margin-bottom: 20px;
    font-weight: 400;
  }

  h2:not(.register-page__form-heading) {
    font-size: 1.75rem;
    font-weight: 400;
    max-width: 600px;
    margin: 3rem auto 1rem auto;

    @media (max-width: 991px) {
      margin-top: 1rem;
    }
  }

  &__form-heading {
    font-size: 1.5rem;
    margin-bottom: 15px;
    font-weight: 600;
    color: #333;
  }

  &__central-message {
    text-align: center;
  }

  &__buttons {
    display: flex;
    flex-flow: row wrap;
    justify-content: center;
    gap: 3rem;
    margin-top: 30px;
  }

  .registration-step__info {
    display: block;
    margin-bottom: 45px;
    font-weight: 400;
    font-size: 1.25rem;
    text-align: center;
    color: #000000;
  }

  &__benefits {
    list-style: none;
    padding-left: 0;

    li {
      display: flex;
      gap: 10px;
      margin-bottom: 1rem;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
}

.log-in {
  text-align: center;
  margin: 40px 0;
}

.competition-redirect {
  text-align: center;
  padding: 40px 0;
}

.btn-secondary {
  background: #fff;
  border: 1px solid #62BBAD !important;
  color: #62BBAD;
  border-radius: 30px;
  padding: auto 30px;
}

.search-button {
  border-radius: 40px;
}
.skip-button {
  border-color: #fff;
  background: none !important;
  &:hover {
    background: none !important;
    border-color: #fff !important;
  }
}
.hide-panel, .bordered-content {
  display: none;
}
</style>
